import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import "bootstrap/dist/css/bootstrap.min.css";

// Brand colors - LemmonTags theme
const COLORS = {
  primary: "#84cc16",
  primaryDark: "#65a30d",
  primaryLight: "#ecfccb",
  background: "#fafafa",
  cardBg: "#ffffff",
  textDark: "#1f2937",
  textMuted: "#6b7280",
  border: "#e5e7eb",
  danger: "#ef4444",
  warning: "#f59e0b",
  success: "#22c55e",
};

const API_URL = process.env.REACT_APP_API_URL || "https://admin-app-lemmon-545109800271.us-central1.run.app";

export default function VetAtenderCita() {
  const { citaId } = useParams();
  const [searchParams] = useSearchParams();
  const vetId = searchParams.get("vetId") || "";
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  // Estados de carga
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Datos de la cita
  const [cita, setCita] = useState(null);
  const [mascota, setMascota] = useState(null);
  const [dueno, setDueno] = useState(null);

  // Formulario
  const [marcarSinRegistro, setMarcarSinRegistro] = useState(false);
  const [peso, setPeso] = useState("");
  const [temperatura, setTemperatura] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [notasPrivadas, setNotasPrivadas] = useState("");

  // Procedimientos agregados
  const [vacunas, setVacunas] = useState([]);
  const [desparasitaciones, setDesparasitaciones] = useState([]);
  const [citasSeguimiento, setCitasSeguimiento] = useState([]);

  // Modales
  const [showVacunaModal, setShowVacunaModal] = useState(false);
  const [showDesparasitacionModal, setShowDesparasitacionModal] = useState(false);
  const [showCitaModal, setShowCitaModal] = useState(false);

  // Auto-guardado
  const LOCAL_STORAGE_KEY = `vetAtender_${citaId}`;

  // Cargar datos de la cita
  useEffect(() => {
    async function loadCita() {
      if (!vetId || !citaId) {
        setError("Faltan parámetros de la cita");
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/veterinarios/${vetId}/citas/${citaId}`);
        if (!res.ok) throw new Error("No se pudo cargar la cita");

        const data = await res.json();
        setCita(data.cita || data);

        // Cargar datos de la mascota si existe objetoId
        if (data.objetoId || data.cita?.objetoId) {
          const objetoId = data.objetoId || data.cita?.objetoId;
          const mascotaRes = await fetch(`${API_URL}/api/veterinarios/${vetId}/pacientes/${objetoId}`);
          if (mascotaRes.ok) {
            const mascotaData = await mascotaRes.json();
            setMascota(mascotaData.mascota || mascotaData);
            setDueno(mascotaData.dueno || mascotaData.propietario);
          }
        }

        // Restaurar borrador si existe
        const draft = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (draft) {
          try {
            const parsed = JSON.parse(draft);
            if (parsed.peso) setPeso(parsed.peso);
            if (parsed.temperatura) setTemperatura(parsed.temperatura);
            if (parsed.observaciones) setObservaciones(parsed.observaciones);
            if (parsed.notasPrivadas) setNotasPrivadas(parsed.notasPrivadas);
            if (parsed.vacunas) setVacunas(parsed.vacunas);
            if (parsed.desparasitaciones) setDesparasitaciones(parsed.desparasitaciones);
            if (parsed.citasSeguimiento) setCitasSeguimiento(parsed.citasSeguimiento);
            toast.success("Borrador restaurado");
          } catch (e) {
            console.error("Error parsing draft:", e);
          }
        }
      } catch (err) {
        console.error("Error loading cita:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    loadCita();
  }, [vetId, citaId, LOCAL_STORAGE_KEY]);

  // Auto-guardado cada 30 segundos
  const saveDraft = useCallback(() => {
    const draft = {
      peso,
      temperatura,
      observaciones,
      notasPrivadas,
      vacunas,
      desparasitaciones,
      citasSeguimiento,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(draft));
  }, [peso, temperatura, observaciones, notasPrivadas, vacunas, desparasitaciones, citasSeguimiento, LOCAL_STORAGE_KEY]);

  useEffect(() => {
    const interval = setInterval(saveDraft, 30000);
    return () => clearInterval(interval);
  }, [saveDraft]);

  // Guardar borrador al cambiar campos importantes
  useEffect(() => {
    const timeout = setTimeout(saveDraft, 2000);
    return () => clearTimeout(timeout);
  }, [peso, temperatura, observaciones, notasPrivadas, saveDraft]);

  // Handlers de modales
  const handleAgregarVacuna = (vacunaData) => {
    setVacunas([...vacunas, { ...vacunaData, id: Date.now() }]);
    setShowVacunaModal(false);
  };

  const handleAgregarDesparasitacion = (despData) => {
    setDesparasitaciones([...desparasitaciones, { ...despData, id: Date.now() }]);
    setShowDesparasitacionModal(false);
  };

  const handleAgregarCitaSeguimiento = (citaData) => {
    setCitasSeguimiento([...citasSeguimiento, { ...citaData, id: Date.now() }]);
    setShowCitaModal(false);
  };

  const handleEliminarVacuna = (id) => {
    setVacunas(vacunas.filter(v => v.id !== id));
  };

  const handleEliminarDesparasitacion = (id) => {
    setDesparasitaciones(desparasitaciones.filter(d => d.id !== id));
  };

  const handleEliminarCitaSeguimiento = (id) => {
    setCitasSeguimiento(citasSeguimiento.filter(c => c.id !== id));
  };

  // Guardar todo
  const handleGuardarTodo = async () => {
    if (marcarSinRegistro) {
      // Opción B: Marcar como completada sin registro
      setSaving(true);
      try {
        const res = await fetch(`${API_URL}/api/veterinarios/${vetId}/citas/${citaId}/completar-sin-registro`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });

        if (!res.ok) throw new Error("No se pudo completar la cita");

        localStorage.removeItem(LOCAL_STORAGE_KEY);
        toast.success("Cita marcada como completada. El dueño recibirá un correo para completar el registro.");
        navigate(`/vetPanel?token=${token}`);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setSaving(false);
      }
      return;
    }

    // Opción A: Guardar con registro completo
    setSaving(true);
    try {
      const payload = {
        citaId,
        objetoId: cita?.objetoId || mascota?.id,
        peso: peso ? parseFloat(peso) : null,
        temperatura: temperatura ? parseFloat(temperatura) : null,
        observaciones,
        notasPrivadas,
        vacunas,
        desparasitaciones,
        citasSeguimiento,
      };

      const res = await fetch(`${API_URL}/api/veterinarios/${vetId}/citas/${citaId}/completar`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "No se pudo guardar la atención");
      }

      localStorage.removeItem(LOCAL_STORAGE_KEY);
      toast.success("Atención registrada exitosamente");
      navigate(`/vetPanel?token=${token}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleVolver = () => {
    navigate(`/vetPanel?token=${token}`);
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: COLORS.background, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="text-center">
          <div className="spinner-border" style={{ color: COLORS.primary }} role="status" />
          <p className="mt-3" style={{ color: COLORS.textMuted }}>Cargando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: COLORS.background, minHeight: "100vh" }}>
        <div className="container py-5">
          <div className="card shadow-sm border-0" style={{ maxWidth: "500px", margin: "0 auto" }}>
            <div className="card-body text-center p-4">
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>Error</div>
              <p style={{ color: COLORS.textMuted }}>{error}</p>
              <button className="btn" style={{ backgroundColor: COLORS.primary, color: "white" }} onClick={handleVolver}>
                Volver al panel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const objetoName = cita?.objetoName || mascota?.nombreMascota || mascota?.nombre || "Paciente";
  const fechaCita = cita?.fecha || new Date().toLocaleDateString("es-MX");

  return (
    <div style={{ backgroundColor: COLORS.background, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ backgroundColor: COLORS.cardBg, borderBottom: `1px solid ${COLORS.border}`, padding: "16px 0" }}>
        <div className="container">
          <button
            className="btn btn-sm d-flex align-items-center gap-2"
            style={{ backgroundColor: "transparent", color: COLORS.textMuted, border: "none" }}
            onClick={handleVolver}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Volver a Mi Agenda
          </button>
        </div>
      </div>

      <div className="container py-4">
        {/* Info del paciente */}
        <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: COLORS.cardBg }}>
          <div className="card-body">
            <div className="d-flex align-items-start gap-3">
              <div style={{
                width: "60px",
                height: "60px",
                borderRadius: "12px",
                backgroundColor: COLORS.primaryLight,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "28px"
              }}>
                {mascota?.fotoUrl ? (
                  <img src={mascota.fotoUrl} alt={objetoName} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "12px" }} />
                ) : ""}
              </div>
              <div className="flex-grow-1">
                <h4 style={{ color: COLORS.textDark, fontWeight: 600, marginBottom: "4px" }}>
                  {objetoName} - Consulta del {fechaCita}
                </h4>
                {cita?.motivo && (
                  <p style={{ color: COLORS.textMuted, margin: 0, fontSize: "14px" }}>
                    Motivo: {cita.motivo}
                  </p>
                )}
                {dueno && (
                  <p style={{ color: COLORS.textMuted, margin: 0, fontSize: "14px" }}>
                    Dueno: {dueno.nombrePropietario || dueno.nombre} - {dueno.telefono || "Sin telefono"}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Documentos PDF */}
        <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: COLORS.cardBg }}>
          <div className="card-header" style={{ backgroundColor: COLORS.primaryLight, borderBottom: "none" }}>
            <h6 className="mb-0" style={{ color: COLORS.primaryDark, fontWeight: 600 }}>
              Documentos
            </h6>
          </div>
          <div className="card-body">
            <div className="d-flex gap-3 flex-wrap">
              <button
                className="btn"
                style={{
                  backgroundColor: cita?.snapshotCartillaUrl ? COLORS.cardBg : "#f3f4f6",
                  color: cita?.snapshotCartillaUrl ? COLORS.textDark : "#9ca3af",
                  border: `1px solid ${COLORS.border}`,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: cita?.snapshotCartillaUrl ? "pointer" : "not-allowed"
                }}
                onClick={() => cita?.snapshotCartillaUrl && window.open(cita.snapshotCartillaUrl, "_blank")}
                disabled={!cita?.snapshotCartillaUrl}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                {cita?.snapshotCartillaUrl ? "Ver Cartilla PDF" : "Cartilla (Generando...)"}
              </button>
              <button
                className="btn"
                style={{
                  backgroundColor: cita?.snapshotExpedienteUrl ? COLORS.cardBg : "#f3f4f6",
                  color: cita?.snapshotExpedienteUrl ? COLORS.textDark : "#9ca3af",
                  border: `1px solid ${COLORS.border}`,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: cita?.snapshotExpedienteUrl ? "pointer" : "not-allowed"
                }}
                onClick={() => cita?.snapshotExpedienteUrl && window.open(cita.snapshotExpedienteUrl, "_blank")}
                disabled={!cita?.snapshotExpedienteUrl}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                {cita?.snapshotExpedienteUrl ? "Ver Expediente PDF" : "Expediente (Generando...)"}
              </button>
            </div>
          </div>
        </div>

        {/* Opción: Marcar sin registro */}
        <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: COLORS.cardBg }}>
          <div className="card-body">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="marcarSinRegistro"
                checked={marcarSinRegistro}
                onChange={(e) => setMarcarSinRegistro(e.target.checked)}
                style={{ cursor: "pointer" }}
              />
              <label className="form-check-label" htmlFor="marcarSinRegistro" style={{ cursor: "pointer" }}>
                <strong>Marcar como atendida sin registrar datos</strong>
                <br />
                <small style={{ color: COLORS.textMuted }}>
                  El dueno recibira un correo para completar el registro
                </small>
              </label>
            </div>
          </div>
        </div>

        {/* Formulario de registro (solo si no marca sin registro) */}
        {!marcarSinRegistro && (
          <>
            {/* Registro de hoy */}
            <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: COLORS.cardBg }}>
              <div className="card-header" style={{ backgroundColor: COLORS.primaryLight, borderBottom: "none" }}>
                <h6 className="mb-0" style={{ color: COLORS.primaryDark, fontWeight: 600 }}>
                  Registro de Hoy
                </h6>
              </div>
              <div className="card-body">
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label" style={{ color: COLORS.textDark, fontWeight: 500 }}>
                      Peso (kg)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      className="form-control"
                      value={peso}
                      onChange={(e) => setPeso(e.target.value)}
                      placeholder="Ej: 8.5"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label" style={{ color: COLORS.textDark, fontWeight: 500 }}>
                      Temperatura (C)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      className="form-control"
                      value={temperatura}
                      onChange={(e) => setTemperatura(e.target.value)}
                      placeholder="Ej: 38.5"
                    />
                  </div>
                </div>

                {/* Procedimientos agregados */}
                <div className="mb-3">
                  <label className="form-label" style={{ color: COLORS.textDark, fontWeight: 500 }}>
                    Procedimientos realizados
                  </label>

                  {/* Lista de vacunas */}
                  {vacunas.length > 0 && (
                    <div className="mb-2">
                      {vacunas.map((v) => (
                        <div key={v.id} className="d-flex align-items-center justify-content-between p-2 mb-1" style={{ backgroundColor: COLORS.primaryLight, borderRadius: "6px" }}>
                          <span style={{ fontSize: "14px" }}>
                            Vacuna: {v.nombre} {v.lote && `(Lote: ${v.lote})`}
                          </span>
                          <button className="btn btn-sm" style={{ color: COLORS.danger }} onClick={() => handleEliminarVacuna(v.id)}>
                            X
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Lista de desparasitaciones */}
                  {desparasitaciones.length > 0 && (
                    <div className="mb-2">
                      {desparasitaciones.map((d) => (
                        <div key={d.id} className="d-flex align-items-center justify-content-between p-2 mb-1" style={{ backgroundColor: "#fef3c7", borderRadius: "6px" }}>
                          <span style={{ fontSize: "14px" }}>
                            Desparasitacion: {d.producto} {d.dosis && `(${d.dosis})`}
                          </span>
                          <button className="btn btn-sm" style={{ color: COLORS.danger }} onClick={() => handleEliminarDesparasitacion(d.id)}>
                            X
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      className="btn btn-sm"
                      style={{ backgroundColor: COLORS.cardBg, color: COLORS.primary, border: `1px solid ${COLORS.primary}` }}
                      onClick={() => setShowVacunaModal(true)}
                    >
                      + Agregar vacuna
                    </button>
                    <button
                      className="btn btn-sm"
                      style={{ backgroundColor: COLORS.cardBg, color: COLORS.warning, border: `1px solid ${COLORS.warning}` }}
                      onClick={() => setShowDesparasitacionModal(true)}
                    >
                      + Agregar desparasitacion
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label" style={{ color: COLORS.textDark, fontWeight: 500 }}>
                    Observaciones generales
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    placeholder="Descripcion de la consulta, hallazgos, tratamiento indicado..."
                  />
                </div>

                <div className="mb-0">
                  <label className="form-label" style={{ color: COLORS.textDark, fontWeight: 500 }}>
                    Notas privadas (solo tu las veras)
                  </label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={notasPrivadas}
                    onChange={(e) => setNotasPrivadas(e.target.value)}
                    placeholder="Notas personales que no apareceran en el expediente del dueno..."
                    style={{ backgroundColor: "#fef2f2" }}
                  />
                  <small style={{ color: COLORS.textMuted }}>
                    Estas notas son privadas y nunca se mostraran al dueno ni en PDFs
                  </small>
                </div>
              </div>
            </div>

            {/* Próximas citas */}
            <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: COLORS.cardBg }}>
              <div className="card-header" style={{ backgroundColor: COLORS.primaryLight, borderBottom: "none" }}>
                <h6 className="mb-0" style={{ color: COLORS.primaryDark, fontWeight: 600 }}>
                  Proximas Citas
                </h6>
              </div>
              <div className="card-body">
                {citasSeguimiento.length > 0 && (
                  <div className="mb-3">
                    {citasSeguimiento.map((c) => (
                      <div key={c.id} className="d-flex align-items-center justify-content-between p-2 mb-1" style={{ backgroundColor: "#dbeafe", borderRadius: "6px" }}>
                        <span style={{ fontSize: "14px" }}>
                          {c.fecha} {c.hora} - {c.motivo}
                        </span>
                        <button className="btn btn-sm" style={{ color: COLORS.danger }} onClick={() => handleEliminarCitaSeguimiento(c.id)}>
                          X
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: COLORS.cardBg, color: COLORS.textDark, border: `1px solid ${COLORS.border}` }}
                  onClick={() => setShowCitaModal(true)}
                >
                  + Agregar cita de seguimiento
                </button>
              </div>
            </div>
          </>
        )}

        {/* Botón guardar */}
        <div className="d-flex justify-content-end mb-4">
          <button
            className="btn btn-lg"
            style={{ backgroundColor: COLORS.primary, color: "white", fontWeight: 600, minWidth: "200px" }}
            onClick={handleGuardarTodo}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Guardando...
              </>
            ) : (
              "Guardar todo"
            )}
          </button>
        </div>
      </div>

      {/* Modales */}
      {showVacunaModal && (
        <AgregarVacunaModal
          onClose={() => setShowVacunaModal(false)}
          onAgregar={handleAgregarVacuna}
        />
      )}

      {showDesparasitacionModal && (
        <AgregarDesparasitacionModal
          onClose={() => setShowDesparasitacionModal(false)}
          onAgregar={handleAgregarDesparasitacion}
        />
      )}

      {showCitaModal && (
        <AgregarCitaSeguimientoModal
          onClose={() => setShowCitaModal(false)}
          onAgregar={handleAgregarCitaSeguimiento}
        />
      )}
    </div>
  );
}

// =============================================================================
// MODAL: Agregar Vacuna
// =============================================================================
function AgregarVacunaModal({ onClose, onAgregar }) {
  const [nombre, setNombre] = useState("");
  const [lote, setLote] = useState("");
  const [laboratorio, setLaboratorio] = useState("");
  const [proximaDosis, setProximaDosis] = useState("");
  const [crearCitaSeguimiento, setCrearCitaSeguimiento] = useState(false);

  // Fecha de hoy para validaciones
  const hoy = new Date().toISOString().split("T")[0];

  const VACUNAS_COMUNES = [
    "Rabia",
    "Parvovirus",
    "Moquillo",
    "Hepatitis",
    "Leptospirosis",
    "Bordetella",
    "Triple Felina",
    "Leucemia Felina",
    "Otra"
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!nombre) {
      toast.error("Selecciona una vacuna");
      return;
    }
    onAgregar({
      nombre,
      lote,
      laboratorio,
      proximaDosis,
      crearCitaSeguimiento,
      fecha: new Date().toISOString().split("T")[0],
    });
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Agregar Vacuna</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Vacuna *</label>
                <select className="form-select" value={nombre} onChange={(e) => setNombre(e.target.value)}>
                  <option value="">Seleccionar...</option>
                  {VACUNAS_COMUNES.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Lote</label>
                <input type="text" className="form-control" value={lote} onChange={(e) => setLote(e.target.value)} />
              </div>
              <div className="mb-3">
                <label className="form-label">Laboratorio</label>
                <input type="text" className="form-control" value={laboratorio} onChange={(e) => setLaboratorio(e.target.value)} />
              </div>
              <div className="mb-3">
                <label className="form-label">Proxima dosis</label>
                <input type="date" className="form-control" value={proximaDosis} onChange={(e) => setProximaDosis(e.target.value)} min={hoy} />
                <small className="text-muted">Debe ser una fecha futura</small>
              </div>
              {proximaDosis && (
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="crearCitaVacuna"
                    checked={crearCitaSeguimiento}
                    onChange={(e) => setCrearCitaSeguimiento(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="crearCitaVacuna">
                    Crear cita de seguimiento para proxima dosis
                  </label>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn" style={{ backgroundColor: COLORS.primary, color: "white" }}>Agregar</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MODAL: Agregar Desparasitación
// =============================================================================
function AgregarDesparasitacionModal({ onClose, onAgregar }) {
  const [producto, setProducto] = useState("");
  const [dosis, setDosis] = useState("");
  const [proximaAplicacion, setProximaAplicacion] = useState("");
  const [crearCitaSeguimiento, setCrearCitaSeguimiento] = useState(false);

  // Fecha de hoy para validaciones
  const hoy = new Date().toISOString().split("T")[0];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!producto) {
      toast.error("Ingresa el producto");
      return;
    }
    onAgregar({
      producto,
      dosis,
      proximaAplicacion,
      crearCitaSeguimiento,
      fecha: new Date().toISOString().split("T")[0],
    });
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Agregar Desparasitacion</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Producto *</label>
                <input type="text" className="form-control" value={producto} onChange={(e) => setProducto(e.target.value)} placeholder="Nombre del producto" />
              </div>
              <div className="mb-3">
                <label className="form-label">Dosis</label>
                <input type="text" className="form-control" value={dosis} onChange={(e) => setDosis(e.target.value)} placeholder="Ej: 1 tableta, 0.5ml" />
              </div>
              <div className="mb-3">
                <label className="form-label">Proxima aplicacion</label>
                <input type="date" className="form-control" value={proximaAplicacion} onChange={(e) => setProximaAplicacion(e.target.value)} min={hoy} />
                <small className="text-muted">Debe ser una fecha futura</small>
              </div>
              {proximaAplicacion && (
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="crearCitaDesp"
                    checked={crearCitaSeguimiento}
                    onChange={(e) => setCrearCitaSeguimiento(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="crearCitaDesp">
                    Crear cita de seguimiento para proxima aplicacion
                  </label>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn" style={{ backgroundColor: COLORS.warning, color: "white" }}>Agregar</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MODAL: Agregar Cita de Seguimiento
// =============================================================================
function AgregarCitaSeguimientoModal({ onClose, onAgregar }) {
  const [motivo, setMotivo] = useState("");
  const [fecha, setFecha] = useState("");
  const [hora, setHora] = useState("");

  // Fecha de hoy para validaciones
  const hoy = new Date().toISOString().split("T")[0];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!motivo || !fecha) {
      toast.error("Completa los campos requeridos");
      return;
    }
    onAgregar({
      motivo,
      fecha,
      hora: hora || "09:00",
    });
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Agregar Cita de Seguimiento</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Motivo *</label>
                <input type="text" className="form-control" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej: Refuerzo de vacuna, Control de peso" />
              </div>
              <div className="mb-3">
                <label className="form-label">Fecha *</label>
                <input type="date" className="form-control" value={fecha} onChange={(e) => setFecha(e.target.value)} min={hoy} />
              </div>
              <div className="mb-3">
                <label className="form-label">Hora</label>
                <input type="time" className="form-control" value={hora} onChange={(e) => setHora(e.target.value)} step="900" />
                <small className="text-muted">Intervalos de 15 minutos</small>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary">Agregar</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
